// =====================================================
// POLICY CALCULATIONS UTILITY
// Handles dynamic calculations and removes hardcoded values
// =====================================================

// Configuration constants (instead of hardcoded values)
export const POLICY_CONFIG = {
  // No Claim Bonus Configuration
  NO_CLAIM_BONUS: {
    APPLICABLE_THRESHOLD: 0, // Claims filed threshold
    BONUS_TIERS: [
      { yearsWithoutClaims: 0, bonusPercentage: 0 },
      { yearsWithoutClaims: 1, bonusPercentage: 5 },
      { yearsWithoutClaims: 2, bonusPercentage: 10 },
      { yearsWithoutClaims: 3, bonusPercentage: 15 },
      { yearsWithoutClaims: 4, bonusPercentage: 20 },
      { yearsWithoutClaims: 5, bonusPercentage: 25 }
    ]
  },
  
  // Default Values (fallbacks for missing data)
  DEFAULTS: {
    COVERAGE_PERCENTAGE: 80,
    DEDUCTIBLE_AMOUNT: 5000,
    CO_PAYMENT_PERCENTAGE: 10,
    ROOM_RENT_LIMIT_PER_DAY: 5000,
    OUT_OF_NETWORK_PERCENTAGE: 60,
    WAITING_PERIODS: {
      PRE_EXISTING: 24,
      MATERNITY: 36,
      CRITICAL_ILLNESS: 90,
      BARIATRIC: 48
    }
  },
  
  // Status Mappings
  STATUS_COLORS: {
    // Policy Statuses
    ACTIVE: 'text-green-600',
    INACTIVE: 'text-red-600',
    EXPIRED: 'text-red-600',
    PENDING: 'text-yellow-600',
    APPROVED: 'text-green-600',
    REJECTED: 'text-red-600',
    
    // Claim Statuses
    DRAFT: 'text-gray-600',
    SUBMITTED: 'text-blue-600',
    UNDER_REVIEW: 'text-yellow-600',
    APPROVED: 'text-green-600',
    REJECTED: 'text-red-600',
    PAID: 'text-green-600',
    CLOSED: 'text-gray-600',
    
    // Additional Statuses
    PENDING_APPROVAL: 'text-yellow-600',
    IN_PROGRESS: 'text-blue-600',
    COMPLETED: 'text-green-600',
    CANCELLED: 'text-red-600',
    FAILED: 'text-red-600'
  }
}

// Calculate No Claim Bonus Percentage
export const calculateNoClaimBonus = (policy) => {
  if (!policy.no_claim_bonus_applicable) {
    return 0
  }

  const yearsSinceStart = getYearsSincePolicyStart(policy.policy_start_date)
  const claimsFiled = policy.total_claims_filed || 0
  
  // If claims were filed, no bonus
  if (claimsFiled > POLICY_CONFIG.NO_CLAIM_BONUS.APPLICABLE_THRESHOLD) {
    return 0
  }

  // Find appropriate bonus tier
  const applicableTier = POLICY_CONFIG.NO_CLAIM_BONUS.BONUS_TIERS
    .slice()
    .reverse()
    .find(tier => yearsSinceStart >= tier.yearsWithoutClaims)

  return applicableTier ? applicableTier.bonusPercentage : 0
}

// Calculate years since policy start
export const getYearsSincePolicyStart = (startDate) => {
  if (!startDate) return 0
  
  const start = new Date(startDate)
  const now = new Date()
  const diffTime = Math.abs(now - start)
  const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365))
  
  return diffYears
}

// Calculate dynamic premium with no claim bonus
export const calculateDynamicPremium = (policy) => {
  const basePremium = policy.annual_premium || 0
  const noClaimBonus = calculateNoClaimBonus(policy)
  
  if (noClaimBonus > 0) {
    const discountAmount = (basePremium * noClaimBonus) / 100
    return Math.max(0, basePremium - discountAmount)
  }
  
  return basePremium
}

// Get dynamic coverage percentage
export const getDynamicCoveragePercentage = (policy) => {
  return policy.coverage_percentage || POLICY_CONFIG.DEFAULTS.COVERAGE_PERCENTAGE
}

// Get dynamic deductible amount
export const getDynamicDeductibleAmount = (policy) => {
  return policy.deductible_amount || POLICY_CONFIG.DEFAULTS.DEDUCTIBLE_AMOUNT
}

// Get dynamic co-payment percentage
export const getDynamicCoPaymentPercentage = (policy) => {
  return policy.co_payment_percentage || POLICY_CONFIG.DEFAULTS.CO_PAYMENT_PERCENTAGE
}

// Get dynamic room rent limit
export const getDynamicRoomRentLimit = (policy) => {
  return policy.room_rent_limit_per_day || POLICY_CONFIG.DEFAULTS.ROOM_RENT_LIMIT_PER_DAY
}

// Get dynamic out of network percentage
export const getDynamicOutOfNetworkPercentage = (policy) => {
  return policy.out_of_network_coverage_percentage || POLICY_CONFIG.DEFAULTS.OUT_OF_NETWORK_PERCENTAGE
}

// Calculate claims statistics dynamically
export const calculateClaimsStatistics = (policy) => {
  const totalClaims = policy.total_claims_filed || 0
  const approvedClaims = policy.total_claims_approved || 0
  const rejectedClaims = policy.total_claims_rejected || 0
  const pendingClaims = policy.total_claims_pending || 0
  
  const approvalRate = totalClaims > 0 ? (approvedClaims / totalClaims) * 100 : 0
  const rejectionRate = totalClaims > 0 ? (rejectedClaims / totalClaims) * 100 : 0
  
  return {
    totalClaims,
    approvedClaims,
    rejectedClaims,
    pendingClaims,
    approvalRate: Math.round(approvalRate),
    rejectionRate: Math.round(rejectionRate),
    totalAmountPaid: policy.total_claim_amount_paid || 0
  }
}

// Get status color dynamically
export const getStatusColor = (status, type = 'policy') => {
  const statusKey = status?.toUpperCase()
  
  if (type === 'policy') {
    return POLICY_CONFIG.STATUS_COLORS[statusKey] || 'text-gray-600'
  }
  
  // For other types, you can extend this
  return POLICY_CONFIG.STATUS_COLORS[statusKey] || 'text-gray-600'
}

// Get status badge color (background + text)
export const getStatusBadgeColor = (status) => {
  const statusKey = status?.toUpperCase()
  
  const badgeColors = {
    // Policy Statuses
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-red-100 text-red-800',
    EXPIRED: 'bg-red-100 text-red-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
    
    // Claim Statuses
    DRAFT: 'bg-gray-100 text-gray-800',
    SUBMITTED: 'bg-blue-100 text-blue-800',
    UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
    PAID: 'bg-green-100 text-green-800',
    CLOSED: 'bg-gray-100 text-gray-800',
    
    // Additional Statuses
    PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800',
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-red-100 text-red-800',
    FAILED: 'bg-red-100 text-red-800'
  }
  
  return badgeColors[statusKey] || 'bg-gray-100 text-gray-800'
}

// Format currency with dynamic locale
export const formatCurrency = (amount, currency = 'INR') => {
  if (!amount || amount === 0) return '₹0'
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0
  }).format(amount)
}

// Format percentage
export const formatPercentage = (value) => {
  if (!value && value !== 0) return 'N/A'
  return `${value}%`
}

// Get dynamic waiting periods
export const getDynamicWaitingPeriods = (policy) => {
  return {
    preExisting: policy.pre_existing_condition_waiting_months || POLICY_CONFIG.DEFAULTS.WAITING_PERIODS.PRE_EXISTING,
    maternity: policy.maternity_waiting_months || POLICY_CONFIG.DEFAULTS.WAITING_PERIODS.MATERNITY,
    criticalIllness: policy.critical_illness_waiting_months || POLICY_CONFIG.DEFAULTS.WAITING_PERIODS.CRITICAL_ILLNESS,
    bariatric: policy.bariatric_surgery_waiting_months || POLICY_CONFIG.DEFAULTS.WAITING_PERIODS.BARIATRIC
  }
}

// Validate and enhance policy data
export const enhancePolicyData = (policy) => {
  if (!policy) return null

  return {
    ...policy,
    // Dynamic calculations
    dynamicNoClaimBonus: calculateNoClaimBonus(policy),
    dynamicPremium: calculateDynamicPremium(policy),
    dynamicCoveragePercentage: getDynamicCoveragePercentage(policy),
    dynamicDeductibleAmount: getDynamicDeductibleAmount(policy),
    dynamicCoPaymentPercentage: getDynamicCoPaymentPercentage(policy),
    dynamicRoomRentLimit: getDynamicRoomRentLimit(policy),
    dynamicOutOfNetworkPercentage: getDynamicOutOfNetworkPercentage(policy),
    claimsStatistics: calculateClaimsStatistics(policy),
    waitingPeriods: getDynamicWaitingPeriods(policy),
    
    // Enhanced formatting
    formattedSumInsured: formatCurrency(policy.sum_insured),
    formattedPremium: formatCurrency(policy.premium_amount),
    formattedAnnualPremium: formatCurrency(policy.annual_premium),
    formattedDeductible: formatCurrency(getDynamicDeductibleAmount(policy)),
    formattedRoomRent: formatCurrency(getDynamicRoomRentLimit(policy)),
    formattedTotalPaid: formatCurrency(policy.total_claim_amount_paid),
    
    // Status colors
    statusColor: getStatusColor(policy.policy_status),
    underwritingStatusColor: getStatusColor(policy.underwriting_status),
    renewalStatusColor: getStatusColor(policy.renewal_status)
  }
}

// Check if policy has claims
export const hasClaims = (policy) => {
  return (policy.total_claims_filed || 0) > 0
}

// Check if policy is eligible for no claim bonus
export const isEligibleForNoClaimBonus = (policy) => {
  return policy.no_claim_bonus_applicable && !hasClaims(policy)
}

// Get policy age in years
export const getPolicyAge = (policy) => {
  return getYearsSincePolicyStart(policy.policy_start_date)
}

// Check if policy is near renewal
export const isNearRenewal = (policy, daysThreshold = 30) => {
  if (!policy.next_renewal_date) return false
  
  const renewalDate = new Date(policy.next_renewal_date)
  const now = new Date()
  const diffTime = renewalDate - now
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  return diffDays <= daysThreshold && diffDays >= 0
}

// Get policy risk level based on claims
export const getPolicyRiskLevel = (policy) => {
  const claimsStats = calculateClaimsStatistics(policy)
  
  if (claimsStats.totalClaims === 0) return 'LOW'
  if (claimsStats.approvalRate >= 80) return 'MEDIUM'
  if (claimsStats.approvalRate >= 60) return 'HIGH'
  return 'VERY_HIGH'
}
